import { Button, Icon, SvgIconNames } from '@toggle/design-system';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Option } from '~/components/filter/Option';
import { FilterOption } from '~/components/update-filter-options/updateFilterOptions';
import { WebSearch } from '~/views/alfred/research/services/conversations-service';
import { useChat } from '~/views/alfred/research/use-chat/useChat';

import { TrustedSourcesModal } from './TrustedSourcesModal';
import * as S from './WebSearchDropdown.styles';

interface DropdownOption {
  key: string;
  name: string;
  icon: SvgIconNames;
  description: string;
  value: {
    web_search: WebSearch;
  };
  isChecked: boolean;
}

export const WebSearchDropdown = () => {
  const { t } = useTranslation(['chat']);

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  const { webSearch, setWebSearch } = useChat(state => ({
    webSearch: state.webSearch,
    setWebSearch: state.setWebSearch,
  }));

  const options: DropdownOption[] = [
    {
      key: 'open',
      name: t('chat:trustedSources.general.title'),
      icon: 'Globe',
      description: t('chat:trustedSources.general.description'),
      value: {
        web_search: 'open',
      },
      isChecked: webSearch === 'open',
    },
    {
      key: 'restricted',
      name: t('chat:trustedSources.restricted.title'),
      icon: 'GlobeLocked',
      description: t('chat:trustedSources.restricted.description'),
      value: {
        web_search: 'restricted',
      },
      isChecked: webSearch === 'restricted',
    },
  ];

  const renderCustomFilterOptionLabel = (filterOption: FilterOption) => {
    return (
      <S.OptionWrapper>
        <Icon iconName={filterOption.icon as SvgIconNames} size={16} />
        <S.Label>{filterOption.name}</S.Label>
      </S.OptionWrapper>
    );
  };

  return (
    <>
      <S.StyledDropdownMenu
        triggerNode={
          <div>
            <Button
              variant="empty"
              iconName={webSearch === 'open' ? 'Globe' : 'GlobeLocked'}
              size="small"
              data-testid="web-search-dropdown-trigger"
            />
          </div>
        }
        open={dropdownOpen}
        onOpenChange={setDropdownOpen}
      >
        {options.map(option => {
          return (
            <Option
              key={option.key}
              option={option}
              isChecked={option.isChecked}
              indentedDescription={24}
              handleClick={() => {
                setWebSearch(option.value.web_search);
              }}
              renderCustomFilterOptionLabel={renderCustomFilterOptionLabel}
            />
          );
        })}
        <S.Divider />
        <S.ViewSources
          data-testid="view-sources-trigger"
          onClick={() => {
            setDropdownOpen(false);
            setModalOpen(true);
          }}
        >
          {t('chat:trustedSources.viewTrustedSources')}
        </S.ViewSources>
      </S.StyledDropdownMenu>
      <TrustedSourcesModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
      />
    </>
  );
};
